import { supabase, Sentry, SupabaseError, withRetry, shouldRethrow } from './shared'
import type { TeslaModel } from '../supabase-types'

// Tesla models API
export const teslaModelsApi = {
  // Get all Tesla models
  async getTeslaModels(options: {
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning []. */
    throwOnError?: boolean
  } = {}): Promise<TeslaModel[]> {
    if (!supabase) {
      return []
    }
    try {
      return await withRetry(async () => {
        const { data, error } = await supabase
          .from('tesla_models')
          .select('*')
          .order('year_start') // Changed from year_from to year_start

        if (error) {
          throw new SupabaseError(`Failed to fetch Tesla models: ${error.message}`, error)
        }

        return (data as TeslaModel[]) || []
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'teslaModelsApi', action: 'getTeslaModels' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return []
    }
  },

  // Get compatible models for a specific year
  async getCompatibleModels(year: number, options: {
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning []. */
    throwOnError?: boolean
  } = {}): Promise<TeslaModel[]> {
    if (!supabase) {
      return []
    }
    try {
      return await withRetry(async () => {
        const { data, error } = await supabase
          .from('tesla_models')
          .select('*')
          .lte('year_start', year) // Changed from year_from to year_start
          .or(`year_end.gte.${year},year_end.is.null`) // Changed from year_to to year_end
          .eq('supports_custom_lock_sound', true) // Changed from supports_custom_sounds

        if (error) {
          throw new SupabaseError(`Failed to fetch compatible models: ${error.message}`, error)
        }

        return (data as TeslaModel[]) || []
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'teslaModelsApi', action: 'getCompatibleModels' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return []
    }
  }
}
